// =============================================================================
// features/sales/campaigns/wizard/components/WizardStepper.tsx — Navegação
// das 6 etapas (F35-S08, doc 26 §6.6). Mesmo padrão visual de nó de
// ImportStepper.tsx (passado=verde+check, atual=azul+glow, futuro=outline) —
// adaptado para 6 passos e navegação CLICÁVEL entre etapas já visitadas
// (DoD: "voltar não perde o preenchido").
//
// Mobile (DoD "empilhada, não comprimida"): abaixo de `md` o nó horizontal
// vira uma barra de progresso compacta com "Etapa X de 6" — 6 círculos de
// 32px lado a lado ficariam ilegíveis/apertados numa tela de 360px.
// =============================================================================

import * as React from 'react';

import { cn } from '../../../../../lib/cn';
import type { WizardStepId } from '../types';
import { WIZARD_STEP_IDS } from '../types';

interface StepDef {
  id: WizardStepId;
  label: string;
}

export const WIZARD_STEPS: StepDef[] = [
  { id: 1, label: 'Objetivo' },
  { id: 2, label: 'Público' },
  { id: 3, label: 'Higiene' },
  { id: 4, label: 'Distribuição' },
  { id: 5, label: 'Cadência' },
  { id: 6, label: 'Revisão' },
];

export interface WizardStepperProps {
  current: WizardStepId;
  /** Etapa mais avançada já alcançada — define até onde o clique navega. */
  furthestReached: WizardStepId;
  onNavigate: (step: WizardStepId) => void;
}

export function WizardStepper({
  current,
  furthestReached,
  onNavigate,
}: WizardStepperProps): React.JSX.Element {
  const currentDef = WIZARD_STEPS.find((s) => s.id === current);

  return (
    <nav aria-label="Etapas da criação de campanha">
      {/* Mobile — barra compacta */}
      <div className="md:hidden flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="font-sans text-xs font-semibold text-ink-3 uppercase tracking-[0.06em]">
            Etapa {current} de {WIZARD_STEP_IDS.length}
          </span>
          <span
            className="font-display font-semibold text-ink"
            style={{ fontSize: 'var(--text-sm)' }}
          >
            {currentDef?.label}
          </span>
        </div>
        <div
          className="h-1.5 w-full rounded-pill overflow-hidden"
          style={{ background: 'var(--surface-muted)' }}
          role="progressbar"
          aria-valuenow={current}
          aria-valuemin={1}
          aria-valuemax={WIZARD_STEP_IDS.length}
        >
          <div
            className="h-full rounded-pill transition-[width] duration-300 ease-out"
            style={{
              width: `${(current / WIZARD_STEP_IDS.length) * 100}%`,
              background: 'var(--grad-azul)',
            }}
          />
        </div>
      </div>

      {/* md+ — nó horizontal clicável */}
      <ol className="hidden md:flex items-center gap-0">
        {WIZARD_STEPS.map((step, idx) => {
          const isPast = step.id < current;
          const isCurrent = step.id === current;
          const isFuture = step.id > current;
          const isReachable = step.id <= furthestReached;
          const isLastStep = idx === WIZARD_STEPS.length - 1;

          return (
            <React.Fragment key={step.id}>
              <li className="flex flex-col items-center gap-2 min-w-[84px]">
                <button
                  type="button"
                  disabled={!isReachable}
                  onClick={() => isReachable && onNavigate(step.id)}
                  aria-current={isCurrent ? 'step' : undefined}
                  aria-label={`Etapa ${step.id}: ${step.label}`}
                  className={cn(
                    'relative flex items-center justify-center',
                    'w-9 h-9 rounded-pill',
                    'font-sans font-bold text-sm',
                    'transition-all duration-[250ms] ease-out',
                    'outline-none focus-visible:ring-2 focus-visible:ring-azul/40 focus-visible:ring-offset-2',
                    isReachable ? 'cursor-pointer' : 'cursor-not-allowed',
                    isFuture && 'text-ink-4 bg-surface-1 border-2 border-border-strong',
                    (isPast || isCurrent) && 'text-white',
                  )}
                  style={
                    isPast
                      ? { background: 'var(--brand-verde)', boxShadow: 'var(--elev-2)' }
                      : isCurrent
                        ? {
                            background: 'var(--brand-azul)',
                            boxShadow: 'var(--glow-azul), var(--elev-2)',
                          }
                        : {}
                  }
                >
                  {isPast ? (
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-3.5 h-3.5"
                      aria-hidden="true"
                    >
                      <path d="M3 8l3.5 3.5L13 5" />
                    </svg>
                  ) : (
                    <span>{step.id}</span>
                  )}
                </button>
                <span
                  className={cn(
                    'font-sans font-semibold text-xs text-center',
                    isCurrent ? 'text-ink' : isPast ? 'text-verde' : 'text-ink-4',
                  )}
                >
                  {step.label}
                </span>
              </li>

              {!isLastStep && (
                <div
                  aria-hidden="true"
                  className="flex-1 h-[2px] mb-6 mx-1"
                  style={{
                    background: isPast
                      ? 'var(--brand-verde)'
                      : isCurrent
                        ? 'linear-gradient(to right, var(--brand-azul) 0%, var(--border-strong) 100%)'
                        : 'var(--border-strong)',
                    opacity: isFuture ? 0.4 : 1,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
